import React, { useState, useEffect } from 'react';

const BASE = 'https://besoins-tat-production.up.railway.app';
const BI = ({ icon, style }) => <i className={`bi bi-${icon}`} style={style} />;

const SwissDot = ({ size = 13 }) => (
  <span style={{ display: 'inline-flex', width: size, height: size, verticalAlign: 'middle' }}>
    <svg viewBox="0 0 32 32" style={{ width: '100%', height: '100%' }}><rect width="32" height="32" rx="6" fill="#E2231A" /><path d="M13.5 6.5H18.5V13.5H25.5V18.5H18.5V25.5H13.5V18.5H6.5V13.5H13.5Z" fill="#fff" /></svg>
  </span>
);

/**
 * Page PUBLIQUE de validation de CV par le candidat (hors authentification).
 * Lien : /cv-validation/:token — API GET/POST /cv-prep/validate/:token.
 */
export default function CVValidationPage({ token }) {
  const [state, setState] = useState('loading'); // loading | ready | done | invalid
  const [data, setData] = useState(null);
  const [comment, setComment] = useState('');
  const [showComment, setShowComment] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`${BASE}/cv-prep/validate/${encodeURIComponent(token)}`);
        if (r.status === 410 || r.status === 404) { if (!cancelled) setState('invalid'); return; }
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const j = await r.json();
        const d = j.data || j;
        if (!cancelled) {
          setData(d);
          if ((d.statut || '').match(/validated|valid/i)) setState('done'); else setState('ready');
        }
      } catch { if (!cancelled) setState('invalid'); }
    })();
    return () => { cancelled = true; };
  }, [token]);

  const cv = data?.structured_cv || {};
  const diff = Array.isArray(data?.diff) ? data.diff : [];
  const poste = data?.poste || '';
  const client = data?.client || '';

  const submit = async (approve) => {
    setError(''); setSubmitting(true);
    try {
      const body = approve ? { approve: true } : { approve: false, comment: comment.trim() };
      const r = await fetch(`${BASE}/cv-prep/validate/${encodeURIComponent(token)}`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
      });
      if (!r.ok) { const t = await r.text().catch(() => ''); throw new Error(`HTTP ${r.status}${t ? ' — ' + t.slice(0, 140) : ''}`); }
      setState('done');
    } catch (e) { setError(`Envoi impossible : ${e.message || e}.`); }
    finally { setSubmitting(false); }
  };

  const page = (children) => (
    <div style={{ minHeight: '100vh', background: '#f2f3f5', color: '#0b0c11', fontFamily: "'IBM Plex Sans', system-ui, sans-serif", display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '40px 16px' }}>
      <div style={{ width: '100%', maxWidth: '720px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '7px', marginBottom: '22px' }}>
          <span style={{ fontFamily: "'Archivo',sans-serif", fontSize: '20px', fontWeight: 800, letterSpacing: '-0.03em' }}>erpia</span>
          <SwissDot />
        </div>
        {children}
        <div style={{ marginTop: '26px', fontSize: '11px', color: '#8a8f9c', textAlign: 'center' }}>Votre CV n'est transmis à personne d'autre que le recruteur qui vous accompagne.</div>
      </div>
    </div>
  );

  const card = { background: '#fff', border: '1px solid #e5e7ec', borderRadius: '16px', padding: 'clamp(22px,4vw,36px)' };

  if (state === 'loading') return page(<div style={{ ...card, textAlign: 'center', color: '#6a6f7b' }}><BI icon="arrow-repeat" style={{ animation: 'spin 1s linear infinite' }} /> Chargement…<style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style></div>);

  if (state === 'invalid') return page(
    <div style={{ ...card, textAlign: 'center' }}>
      <div style={{ width: '48px', height: '48px', borderRadius: '14px', background: 'rgba(226,35,26,0.10)', color: '#E2231A', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 14px', fontSize: '22px' }}><BI icon="exclamation-triangle" /></div>
      <div style={{ fontSize: '17px', fontWeight: 700 }}>Lien invalide ou expiré</div>
      <div style={{ marginTop: '7px', fontSize: '13.5px', color: '#6a6f7b' }}>Ce lien de validation n'est plus disponible. Contactez votre interlocuteur pour un nouveau lien.</div>
    </div>
  );

  if (state === 'done') return page(
    <div style={{ ...card, textAlign: 'center' }}>
      <div style={{ width: '52px', height: '52px', borderRadius: '14px', background: 'rgba(45,200,110,0.16)', color: '#16a34a', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 14px', fontSize: '24px' }}><BI icon="check-lg" /></div>
      <div style={{ fontSize: '18px', fontWeight: 700 }}>Merci !</div>
      <div style={{ marginTop: '7px', fontSize: '13.5px', color: '#6a6f7b' }}>Votre retour a bien été enregistré. Le recruteur en tient compte pour la suite.</div>
    </div>
  );

  const H = ({ children }) => <div style={{ fontSize: '10.5px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: '#8a8f9c', margin: '16px 0 6px' }}>{children}</div>;

  return page(<>
    <div style={{ ...card }}>
      <div style={{ fontSize: '10.5px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: '#8a8f9c' }}>Validation de votre CV</div>
      <div style={{ fontFamily: "'Archivo',sans-serif", fontSize: 'clamp(21px,3vw,27px)', fontWeight: 800, letterSpacing: '-0.02em', marginTop: '4px' }}>{poste || 'Votre CV'}<span style={{ color: '#E2231A' }}>.</span></div>
      {client && <div style={{ marginTop: '5px', fontSize: '13.5px', color: '#6a6f7b' }}>{client}</div>}
      <div style={{ marginTop: '10px', fontSize: '13.5px', color: '#4a4f5b', lineHeight: 1.55 }}>Voici la version de votre CV préparée pour ce poste. Relisez-la, puis validez-la ou signalez-nous vos remarques.</div>

      {/* Modifications apportées */}
      {diff.length > 0 && (
        <>
          <H>Ce qui a été ajusté</H>
          <ul style={{ margin: 0, paddingLeft: '16px', fontSize: '12.5px', color: '#4a4f5b', lineHeight: 1.55 }}>
            {diff.slice(0, 12).map((d, i) => <li key={i} style={{ marginBottom: '3px' }}><b>{d.section}</b>{d.type ? ` — ${d.type.toLowerCase()}` : ''}</li>)}
          </ul>
        </>
      )}

      {/* CV en lecture seule */}
      <div style={{ marginTop: '18px', border: '1px solid #e5e7ec', borderRadius: '12px', padding: '18px', background: '#fbfbfc' }}>
        <div style={{ fontSize: '16px', fontWeight: 700 }}>{cv.name || '—'}</div>
        <div style={{ fontSize: '13px', color: '#6a6f7b' }}>{cv.title || ''}</div>
        {cv.profile && (<><H>Profil</H><div style={{ fontSize: '12.5px', color: '#3a3f4b', lineHeight: 1.55 }}>{cv.profile}</div></>)}
        {Array.isArray(cv.skills?.technical_skills) && cv.skills.technical_skills.length > 0 && (
          <><H>Compétences</H><div style={{ display: 'flex', flexWrap: 'wrap', gap: '5px' }}>{cv.skills.technical_skills.map((s, i) => <span key={i} style={{ fontSize: '11.5px', padding: '2px 8px', borderRadius: '6px', background: '#eef0f3', color: '#4a4f5b' }}>{s}</span>)}</div></>
        )}
        {Array.isArray(cv.experience) && cv.experience.length > 0 && (
          <><H>Expérience</H>
          {cv.experience.map((e, i) => (
            <div key={i} style={{ marginBottom: '10px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: '8px' }}>
                <span style={{ fontSize: '13px', fontWeight: 600 }}>{e.position || '—'}</span>
                <span style={{ fontSize: '11px', color: '#8a8f9c' }}>{[e.start_date, e.end_date].filter(Boolean).join(' – ')}</span>
              </div>
              <div style={{ fontSize: '12px', color: '#6a6f7b' }}>{[e.company, e.location].filter(Boolean).join(' · ')}</div>
              {Array.isArray(e.tasks) && e.tasks.length > 0 && <ul style={{ margin: '4px 0 0', paddingLeft: '16px', fontSize: '12px', color: '#3a3f4b' }}>{e.tasks.map((t, j) => <li key={j}>{t}</li>)}</ul>}
            </div>
          ))}</>
        )}
      </div>

      {error && <div style={{ marginTop: '14px', fontSize: '12.5px', color: '#E2231A' }}><BI icon="exclamation-circle" /> {error}</div>}

      {!showComment ? (
        <div style={{ display: 'flex', gap: '10px', marginTop: '20px', flexWrap: 'wrap' }}>
          <button onClick={() => submit(true)} disabled={submitting} style={{ flex: 1, minWidth: '200px', padding: '13px', borderRadius: '11px', border: 'none', background: '#16a34a', color: '#fff', fontFamily: 'inherit', fontSize: '14px', fontWeight: 700, cursor: 'pointer', opacity: submitting ? 0.7 : 1 }}><BI icon="check-lg" /> Je valide mon CV</button>
          <button onClick={() => setShowComment(true)} disabled={submitting} style={{ flex: 1, minWidth: '200px', padding: '13px', borderRadius: '11px', border: '1px solid #d8dbe1', background: '#fff', color: '#0b0c11', fontFamily: 'inherit', fontSize: '14px', fontWeight: 700, cursor: 'pointer' }}><BI icon="chat-left-text" /> J'ai des remarques</button>
        </div>
      ) : (
        <div style={{ marginTop: '20px' }}>
          <textarea value={comment} onChange={e => setComment(e.target.value)} rows={4} autoFocus placeholder="Vos remarques (une expérience mal décrite, une compétence oubliée…)"
            style={{ width: '100%', boxSizing: 'border-box', padding: '11px 13px', borderRadius: '10px', border: '1px solid #d8dbe1', background: '#fff', color: '#0b0c11', fontFamily: 'inherit', fontSize: '14px', lineHeight: 1.5, resize: 'vertical', outline: 'none' }} />
          <div style={{ display: 'flex', gap: '10px', marginTop: '10px' }}>
            <button onClick={() => submit(false)} disabled={submitting || !comment.trim()} style={{ flex: 1, padding: '12px', borderRadius: '11px', border: 'none', background: '#E2231A', color: '#fff', fontFamily: 'inherit', fontSize: '14px', fontWeight: 700, cursor: 'pointer', opacity: (submitting || !comment.trim()) ? 0.6 : 1 }}>Envoyer mes remarques</button>
            <button onClick={() => setShowComment(false)} disabled={submitting} style={{ padding: '12px 16px', borderRadius: '11px', border: '1px solid #d8dbe1', background: '#fff', color: '#0b0c11', fontFamily: 'inherit', fontSize: '14px', fontWeight: 600, cursor: 'pointer' }}>Retour</button>
          </div>
        </div>
      )}
    </div>
  </>);
}
